import { Button } from '@/components/ui/button';
import { X, MapPin, Store, StickyNote } from 'lucide-react';

export interface DeliveryOfferItem {
  name: string;
  quantity: number;
}

export interface DeliveryOffer {
  id: string;
  restaurantName: string;
  restaurantAddress?: string;
  deliveryAddress: string;
  itemsCount?: number;
  distanceKm?: number | null;
  /** The delivery fee the driver earns */
  payout: number;
  /** Order total including the delivery fee */
  orderTotal?: number;
  /** Delivery fee charged to the customer */
  deliveryFee?: number;
  /** True when the customer already paid online (e.g. Wave) */
  isPaid?: boolean;
  items?: DeliveryOfferItem[];
  customerName?: string;
  notes?: string | null;
  createdAt?: string;
}

interface DeliveryOfferPopupProps {
  offer: DeliveryOffer;
  /** Freelance drivers can accept/decline. Fleet drivers only see details. */
  canDecide: boolean;
  onAccept: (offerId: string) => void;
  onDecline: (offerId: string) => void;
  onDismiss?: () => void;
  accepting?: boolean;
}

const DeliveryOfferPopup = ({
  offer,
  canDecide,
  onAccept,
  onDecline,
  onDismiss,
  accepting,
}: DeliveryOfferPopupProps) => {
  const itemsCount =
    offer.itemsCount ?? offer.items?.reduce((sum, i) => sum + (i.quantity || 1), 0);

  return (
    <div className="fixed inset-0 z-[2500] flex flex-col justify-end pointer-events-none">
      {/* Decline pill on the right side of the screen */}
      {canDecide && (
        <button
          onClick={() => onDecline(offer.id)}
          className="pointer-events-auto absolute top-4 right-4 bg-white shadow-lg rounded-full px-5 py-2.5 text-base font-bold text-gray-900 active:scale-95 transition-transform"
        >
          Decline
        </button>
      )}
      {!canDecide && onDismiss && (
        <button
          onClick={onDismiss}
          className="pointer-events-auto absolute top-4 right-4 bg-white shadow-lg rounded-full p-2.5 text-gray-900"
          aria-label="Hide order details"
        >
          <X className="h-5 w-5" />
        </button>
      )}

      <div className="pointer-events-auto bg-white rounded-t-3xl shadow-2xl p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] animate-in slide-in-from-bottom duration-300 max-h-[85vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-gray-500">
              {canDecide ? 'New delivery offer' : 'Your assigned delivery'}
            </p>
            <h3 className="text-xl font-bold truncate">{offer.restaurantName}</h3>
            <p className="text-sm text-gray-500">
              {itemsCount ? `${itemsCount} item${itemsCount > 1 ? 's' : ''}` : 'Delivery'}
              {offer.distanceKm ? ` • ${offer.distanceKm.toFixed(1)} km total` : ''}
            </p>
          </div>
        </div>

        <div className="my-4 border-t border-gray-200" />

        <div className="text-center">
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <p className="text-4xl font-extrabold text-gray-900">
              D{Number(offer.orderTotal ?? 0).toFixed(2)}
              {canDecide && (
                <span className="text-xl font-bold text-red-600 ml-1">
                  (D{offer.payout.toFixed(2)})
                </span>
              )}
            </p>
            {offer.isPaid && (
              <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full tracking-wide">
                PAID
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Order total (incl. delivery fee)</p>
        </div>

        {offer.items && offer.items.length > 0 && (
          <div className="mt-4 rounded-2xl bg-gray-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Order details
            </p>
            <ul className="space-y-1.5">
              {offer.items.map((item, idx) => (
                <li key={`${item.name}-${idx}`} className="flex items-start gap-2 text-sm text-gray-800">
                  <span className="font-bold text-gray-500 shrink-0">{item.quantity}×</span>
                  <span className="min-w-0">{item.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 space-y-3">
          <div className="flex items-start gap-3">
            <Store className="h-4 w-4 text-red-600 mt-1 shrink-0" />
            <p className="text-sm text-gray-700 line-clamp-2">
              {offer.restaurantAddress || offer.restaurantName}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 text-green-600 mt-1 shrink-0" />
            <p className="text-sm text-gray-700 line-clamp-2">
              {offer.deliveryAddress}
              {offer.customerName ? ` • ${offer.customerName}` : ''}
            </p>
          </div>
          {offer.notes && (
            <div className="flex items-start gap-3">
              <StickyNote className="h-4 w-4 text-amber-600 mt-1 shrink-0" />
              <p className="text-sm text-gray-700">{offer.notes}</p>
            </div>
          )}
        </div>

        {canDecide && (
          <Button
            onClick={() => onAccept(offer.id)}
            disabled={accepting}
            className="mt-5 w-full h-14 rounded-full text-lg font-bold bg-red-600 hover:bg-red-700 text-white"
          >
            {accepting ? 'Accepting…' : 'Accept'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default DeliveryOfferPopup;
